import { toast } from "@/v2-ui/toast"
import ActionMenu from "@/v2-backoffice-client/components/actionMenu"
import { capabilitySheetHref } from "@/v2-backoffice-client/license/changes/license.changes.constants"
import { useCapabilityDecisionWriter } from "@/v2-backoffice-client/license/changes/license.changes.decide.hooks"
import {
  decisionOf,
  displayNameOf,
  type LicenseChange,
} from "@/v2-backoffice-client/license/changes/license.changes.utils"

export type LicenseChangeRowToolbarProps = {
  change: LicenseChange
}

// The row's menu on the Capabilities view: open the panel, and the one
// decision that needs no further input, "Free for everyone", so a list of
// new capabilities can be triaged without opening each. Package-only needs
// the packages and the lock's blast radius in view, so it goes through the
// panel; taking a decision back is confirmed, since for a package-only
// capability it is an unlock.
function LicenseChangeRowToolbar(props: LicenseChangeRowToolbarProps) {
  const { change } = props
  const { write, isSaving } = useCapabilityDecisionWriter()
  const decision = decisionOf(change)
  const name = displayNameOf(change)
  const panelHref = capabilitySheetHref(change.SurfaceCode)

  return (
    <ActionMenu>
      <ActionMenu.Trigger />
      <ActionMenu.Content>
        <ActionMenu.LinkItem
          iconSrc="expand"
          to={panelHref}
          data-feature-code="UI_BackofficeWhatsNew_Row_Open"
        >
          Open
        </ActionMenu.LinkItem>

        {change.IsInCatalog && (
          <>
            <ActionMenu.Separator />
            <ActionMenu.Item
              iconSrc="unlock"
              disabled={decision === "everyone"}
              isLoading={isSaving}
              data-feature-code="UI_BackofficeWhatsNew_Row_Free"
              onSelect={() => {
                void write(change.SurfaceCode, "everyone").then((saved) => {
                  if (saved) toast({ title: `${name} is free for everyone.` })
                })
              }}
            >
              Free for everyone
            </ActionMenu.Item>
            <ActionMenu.LinkItem
              iconSrc="lock"
              to={panelHref}
              data-feature-code="UI_BackofficeWhatsNew_Row_Packages"
            >
              Package-only…
            </ActionMenu.LinkItem>
            {decision !== null && (
              <ActionMenu.ConfirmItem
                iconSrc="undo"
                isLoading={isSaving}
                confirmTitle="Take the decision back"
                confirmDescription={
                  decision === "packages"
                    ? `${name} goes back to free for everyone, for every customer, and waits on a decision again.`
                    : `${name} stays free for everyone, but waits on a decision again.`
                }
                confirmText="Take it back"
                data-feature-code="UI_BackofficeWhatsNew_Row_Undo"
                onConfirm={async () => {
                  const saved = await write(change.SurfaceCode, "undecided")
                  if (saved) {
                    toast({ title: `${name} waits on a decision again.` })
                  }
                }}
              >
                Take the decision back
              </ActionMenu.ConfirmItem>
            )}
          </>
        )}
      </ActionMenu.Content>
    </ActionMenu>
  )
}

export default LicenseChangeRowToolbar
